'use strict';

/**
 * Product Page — Alpine.js component
 * Handles: SKU selection, price update, quantity, services, add-to-cart AJAX
 */

document.addEventListener('alpine:init', () => {
    Alpine.data('productPage', () => {
        const config = window.__PRODUCT__ || {};
        const skus = config.skus || {};
        const currency = config.currency || {};
        const featuresMap = config.featuresSelectable || {};
        const ignoreStock = !!config.ignoreStockCount;
        const denominator = parseFloat(config.denominator) || 1;

        // SKU is purchasable: available && (ignoreStock || count is null || count > 0)
        const isAvailable = (sku) => {
            if (sku.available === false) return false;
            if (ignoreStock) return true;
            const count = sku.count ?? null;
            return count === null || count > 0;
        };

        // Find initial SKU
        const initialSkuId = config.currentSkuId;
        const initialSku = skus[initialSkuId] || Object.values(skus)[0] || {};

        return {
            // State
            currentSkuId: initialSkuId,
            currentPrice: parseFloat(initialSku.price) || 0,
            currentComparePrice: parseFloat(initialSku.compare_price) || 0,
            currentArticle: initialSku.sku || config.productSkuCode || '',
            currentAvailable: isAvailable(initialSku),
            currentCount: initialSku.count ?? null,
            currentMin: parseFloat(initialSku.order_count_min) || 1,
            currentStep: parseFloat(initialSku.order_count_step) || 1,
            quantity: parseFloat(initialSku.order_count_min) || 1,
            added: false,
            loading: false,
            stockUnit: config.stockUnit || '',
            baseUnit: config.baseUnit || '',
            stockBaseRatio: parseFloat(config.stockBaseRatio) || 0,
            selectedFeatures: {},
            selectedFeatureTitles: {},
            selectedServices: {},
            stickyVisible: false,

            init() {
                // Initialize selected features from first values
                if (config.skuType === 1) {
                    document.querySelectorAll('.sku-feature').forEach(input => {
                        this.selectedFeatures[input.dataset.feature] = input.value;
                    });
                    // Initialize titles from first active buttons
                    document.querySelectorAll('.product-cart__feature-btn--active').forEach(btn => {
                        const code = btn.dataset.feature;
                        const title = btn.querySelector('.product-cart__feature-text')?.textContent?.trim() || btn.getAttribute('title') || '';
                        if (code) this.selectedFeatureTitles[code] = title;
                    });
                }

                // Sync services UI state: select is active only when checkbox is checked.
                document.querySelectorAll('.product-cart__service').forEach((serviceEl) => {
                    const checkbox = serviceEl.querySelector('input[type="checkbox"][data-service-id]');
                    const select = serviceEl.querySelector('.product-cart__service-select');
                    if (!checkbox || !select) return;

                    select.disabled = !checkbox.checked;
                    if (checkbox.checked) {
                        const selectedOption = select.options[select.selectedIndex];
                        this.selectedServices[checkbox.dataset.serviceId] = parseFloat(selectedOption?.dataset.price) || 0;
                    }
                });

                // Sticky-bar: показываем, когда submit-кнопка ушла ВВЕРХ из viewport.
                this.$nextTick(() => {
                    const submitBtn = document.querySelector('.product-cart__submit');
                    if (!submitBtn || !('IntersectionObserver' in window)) return;
                    const io = new IntersectionObserver((entries) => {
                        const e = entries[0];
                        this.stickyVisible = !e.isIntersecting && e.boundingClientRect.top < 0;
                    }, { rootMargin: '-80px 0px 0px 0px', threshold: 0 });
                    io.observe(submitBtn);
                });
            },

            scrollTop() {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            },

            scrollToCart() {
                const btn = document.querySelector('.product-cart__submit');
                if (!btn) return;
                btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
            },

            // ── Computed ──

            get formattedPrice() {
                return this.formatCurrency(this.totalPrice);
            },

            get formattedComparePrice() {
                if (this.currentComparePrice <= 0) return '';
                return this.formatCurrency(this.currentComparePrice);
            },

            get stockState() {
                if (!this.currentAvailable) return 'out';
                if (this.currentCount !== null && this.currentCount > 0 && this.currentCount <= 5) return 'low';
                return 'in';
            },

            get stockLabel() {
                const state = this.stockState;
                if (state === 'out') return 'Нет в наличии';
                if (state === 'low') return 'Мало';
                return 'В наличии';
            },

            get installmentText() {
                const c = config.installment || {};
                if (!c.enabled) return '';
                if (c.mode === 'formula' && c.months > 0) {
                    const perMonth = Math.round(this.currentPrice / c.months);
                    const sep = currency.thousands_sep || ' ';
                    const formatted = String(perMonth).replace(/\B(?=(\d{3})+(?!\d))/g, sep);
                    return `${c.prefix || 'От'} ${formatted} ${c.suffix || '₽/мес'}`;
                }
                return c.text || 'Рассрочка';
            },

            get discountPercent() {
                if (this.currentComparePrice <= 0) return '';
                const pct = Math.round((this.currentComparePrice - this.currentPrice) / this.currentComparePrice * 100);
                return `\u2212${pct}%`;
            },

            get totalPrice() {
                let total = this.currentPrice;
                for (const key in this.selectedServices) {
                    total += this.selectedServices[key];
                }
                return total;
            },

            get formattedBasePrice() {
                if (!this.stockBaseRatio) return '';
                return this.formatCurrency(this.currentPrice / this.stockBaseRatio);
            },

            // ── Actions ──

            selectFeature(featureCode, value, btnEl) {
                this.selectedFeatures[featureCode] = value;
                this.selectedFeatureTitles[featureCode] = btnEl.querySelector('.product-cart__feature-text')?.textContent?.trim() || btnEl.getAttribute('title') || value;

                // Update hidden input
                const hiddenInput = document.querySelector(`.sku-feature[data-feature="${featureCode}"]`);
                if (hiddenInput) hiddenInput.value = value;

                // Update button active state
                const parent = btnEl.closest('.product-cart__feature-values');
                if (parent) {
                    parent.querySelectorAll('.product-cart__feature-btn').forEach(btn => {
                        btn.classList.toggle('product-cart__feature-btn--active', btn === btnEl);
                    });
                }

                // Build feature key and lookup SKU
                this.lookupSkuByFeatures();
            },

            lookupSkuByFeatures() {
                // Build key in Shop-Script format: "feature_id:value_id;feature_id:value_id;"
                let key = '';
                document.querySelectorAll('.sku-feature').forEach(input => {
                    key += input.dataset.feature + ':' + input.value + ';';
                });

                const match = featuresMap[key];
                if (match) {
                    this.currentSkuId = match.id;
                    this.currentPrice = parseFloat(match.price) || 0;
                    this.currentComparePrice = parseFloat(match.compare_price) || 0;

                    // Get count/min/step from full SKU data (featuresMap may lack count)
                    const fullSku = skus[match.id];
                    if (fullSku) {
                        this.currentCount = fullSku.count ?? null;
                        this.currentMin = parseFloat(fullSku.order_count_min) || 1;
                        this.currentStep = parseFloat(fullSku.order_count_step) || 1;
                        this.stockBaseRatio = parseFloat(fullSku.stock_base_ratio) || 0;
                    } else {
                        this.currentCount = match.count ?? null;
                    }
                    this.currentArticle = (fullSku && fullSku.sku) || match.sku || config.productSkuCode || '';
                    this.$dispatch('product-sku-changed', { article: this.currentArticle });
                    this.currentAvailable = isAvailable(fullSku || match);
                    this.quantity = this.currentMin;

                    if (match.image_id && window.__productGallery) {
                        window.__productGallery.slideTo(match.image_id);
                    }
                } else {
                    // Invalid feature combination: keep last price, but block purchase.
                    this.currentAvailable = false;
                    this.currentCount = 0;
                }

                this.added = false;
            },

            selectSku(radioEl) {
                const price = parseFloat(radioEl.dataset.price) || 0;
                const comparePrice = parseFloat(radioEl.dataset.comparePrice) || 0;
                const imageId = parseInt(radioEl.dataset.imageId) || 0;

                const skuId = parseInt(radioEl.value);
                this.currentSkuId = skuId;
                this.currentPrice = price;
                this.currentComparePrice = comparePrice;
                // Get count/min/step from full SKU data (null = unlimited stock)
                const fullSku = skus[skuId];
                if (fullSku) {
                    this.currentCount = fullSku.count ?? null;
                    this.currentMin = parseFloat(fullSku.order_count_min) || 1;
                    this.currentStep = parseFloat(fullSku.order_count_step) || 1;
                    this.stockBaseRatio = parseFloat(fullSku.stock_base_ratio) || 0;
                } else {
                    const raw = radioEl.dataset.count;
                    this.currentCount = raw !== '' && raw != null ? parseInt(raw) : null;
                }
                this.currentArticle = (fullSku && fullSku.sku) || config.productSkuCode || '';
                this.$dispatch('product-sku-changed', { article: this.currentArticle });
                this.currentAvailable = fullSku
                    ? isAvailable(fullSku)
                    : (!radioEl.disabled && (ignoreStock || this.currentCount === null || this.currentCount > 0));
                this.quantity = this.currentMin;

                // Highlight active item
                document.querySelectorAll('.product-cart__sku-item').forEach(item => {
                    item.classList.toggle('product-cart__sku-item--active',
                        item.querySelector('input')?.value == radioEl.value);
                });

                // Update gallery
                if (imageId && window.__productGallery) {
                    window.__productGallery.slideTo(imageId);
                }

                this.added = false;
            },

            incrementQty() {
                const qty = +this.quantity || this.currentMin;
                const max = (ignoreStock || !this.currentCount) ? 9999 : this.currentCount;
                let next = +(qty + this.currentStep).toFixed(4);
                if (denominator !== 1) next = Math.round(next / denominator) * denominator;
                if (next <= max) this.quantity = +next.toFixed(4);
            },

            decrementQty() {
                const qty = +this.quantity || this.currentMin;
                let prev = +(qty - this.currentStep).toFixed(4);
                if (denominator !== 1) prev = Math.round(prev / denominator) * denominator;
                if (prev >= this.currentMin) this.quantity = +prev.toFixed(4);
            },

            toggleService(el) {
                const serviceId = el.dataset.serviceId;
                const select = el.closest('.product-cart__service')
                    ?.querySelector('.product-cart__service-select');

                if (el.checked) {
                    const price = parseFloat(el.dataset.price) || 0;
                    if (select) select.disabled = false;
                    if (select) {
                        const selectedOption = select.options[select.selectedIndex];
                        this.selectedServices[serviceId] = parseFloat(selectedOption?.dataset.price) || 0;
                    } else {
                        this.selectedServices[serviceId] = price;
                    }
                } else {
                    if (select) select.disabled = true;
                    delete this.selectedServices[serviceId];
                }
            },

            updateServiceVariant(selectEl) {
                const serviceId = selectEl.dataset.serviceId;
                const checkbox = selectEl.closest('.product-cart__service')
                    ?.querySelector(`input[data-service-id="${serviceId}"]`);
                if (checkbox?.checked) {
                    const selectedOption = selectEl.options[selectEl.selectedIndex];
                    this.selectedServices[serviceId] = parseFloat(selectedOption?.dataset.price) || 0;
                }
            },

            async addToCart(event) {
                if (!this.currentAvailable || this.loading) return;

                const form = event.target;
                const formData = new FormData(form);
                formData.set('sku_id', this.currentSkuId);
                formData.set('quantity', this.quantity);

                this.loading = true;
                try {
                    const response = await fetch(form.action, {
                        method: 'POST',
                        body: formData,
                        headers: { 'X-Requested-With': 'XMLHttpRequest' },
                    });

                    if (response.ok) {
                        const data = await response.json().catch(() => null);
                        if (data?.status === 'fail') {
                            window.dispatchEvent(new CustomEvent('cart-toast', {
                                detail: { name: data.errors || 'Ошибка добавления', type: 'error' },
                            }));
                            return;
                        }
                        this.added = true;

                        // Update cart badges (header + bottom-bar) and notify mini-cart
                        if (data?.data?.count != null) window.updateCartBadges(data.data.count);
                        else window.dispatchEvent(new Event('cart-updated'));

                        // Auto-hide after 5s
                        setTimeout(() => { this.added = false; }, 5000);
                    }
                } catch { /* silent */ }
                finally { this.loading = false; }
            },

            // ── Helpers ──

            formatCurrency(amount) {
                const sep = currency.thousands_sep || ' ';
                const dec = currency.decimal_point || ',';
                const precision = parseInt(currency.frac_digits ?? currency.precision) || 0;
                const raw = currency.sign_html || currency.sign || '₽';
                const sign = raw.replace(/<[^>]*>/g, '');
                const signDelim = currency.sign_delim ?? ' ';

                let i = parseInt(amount = (+amount || 0).toFixed(precision)) + '';
                const j = i.length > 3 ? i.length % 3 : 0;
                const km = j ? i.substr(0, j) + sep : '';
                const kw = i.substr(j).replace(/(\d{3})(?=\d)/g, '$1' + sep);
                const kd = precision && (amount - i)
                    ? dec + Math.abs(amount - i).toFixed(precision).replace(/-/, 0).slice(2)
                    : '';
                const number = km + kw + kd;

                return currency.sign_position
                    ? number + signDelim + sign
                    : sign + signDelim + number;
            },
        };
    });
});

// ── Recently viewed: write product ID to shop_view cookie ────────────────────
(function () {
    const pid = (window.__PRODUCT__ || {}).productId;
    if (!pid) return;

    const match = document.cookie.match(/(?:^|;\s*)shop_view=([^;]*)/);
    const ids = match ? match[1].split(',').filter(Boolean) : [];

    if (!ids.includes(String(pid))) {
        ids.unshift(String(pid));
    }

    const out = ids.slice(0, 16).join(',');
    const expires = new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `shop_view=${out}; expires=${expires}; path=/`;
}());
